import * as React from "react";
import { toast } from "react-toastify";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useAddSubcollectionMembersMutation,
  useCreateSubcollectionMutation,
  useGetSubcollectionsQuery,
} from "@/modules/snapshots/snapshotCollection.api";
import type { Subcollection } from "@/modules/snapshots/snapshotCollection.types";
import { ButtonProps } from "@/components/ui/button";

interface Props {
  snapshotCollectionId: string;
  selectedSnapshotIds: string[];
  disabled?: boolean;
  size?: ButtonProps["size"];
  onSubcollectionCreated?: (subcollection: Subcollection) => void;
  contractFamilyProjection?: boolean;
}

const getApiError = (error: unknown): { status?: number; detail?: string } => {
  const apiError = error as {
    status?: number;
    data?: { detail?: string };
    response?: { status?: number; data?: { detail?: string } };
  };

  return {
    status: apiError.status ?? apiError.response?.status,
    detail: apiError.data?.detail ?? apiError.response?.data?.detail,
  };
};

export const AddToSubcollection = ({
  snapshotCollectionId,
  selectedSnapshotIds,
  disabled,
  size,
  onSubcollectionCreated,
  contractFamilyProjection = false,
}: Props) => {
  const { data: subcollections = [] } = useGetSubcollectionsQuery({
    snapshot_collection_id: snapshotCollectionId,
    contract_family_projection: contractFamilyProjection,
  });
  const [addMembers, { isLoading }] = useAddSubcollectionMembersMutation();
  const [createSubcollection, { isLoading: isCreating }] = useCreateSubcollectionMutation();
  const [isCreatingSubcollection, setIsCreatingSubcollection] = React.useState(false);
  const [newSubcollectionName, setNewSubcollectionName] = React.useState("");

  const onPick = async (id: string, name: string) => {
    try {
      await addMembers({
        id,
        business_report_outcome_ids: selectedSnapshotIds,
        contract_family_projection: contractFamilyProjection,
      }).unwrap();
      toast.success(`Added ${selectedSnapshotIds.length} snapshot(s) to "${name}"`);
    } catch (error) {
      const apiError = getApiError(error);
      if (apiError.status === 409) {
        toast.warn(apiError.detail ?? "Selected snapshot(s) are already in this sub-collection");
        return;
      }
      toast.error("Failed to add snapshots to sub-collection. Please try again.");
    }
  };

  const resetCreateState = () => {
    setIsCreatingSubcollection(false);
    setNewSubcollectionName("");
  };

  const onCreate = async () => {
    const name = newSubcollectionName.trim();
    if (!name) return;

    try {
      const createdSubcollection = await createSubcollection({
        snapshot_collection_id: snapshotCollectionId,
        name,
        business_report_outcome_ids: selectedSnapshotIds,
        contract_family_projection: contractFamilyProjection,
      }).unwrap();
      toast.success(`Created "${name}" and added ${selectedSnapshotIds.length} snapshot(s)`);
      resetCreateState();
      onSubcollectionCreated?.(createdSubcollection);
    } catch {
      toast.error("Failed to create sub-collection. Please try again.");
    }
  };

  return (
    <DropdownMenu onOpenChange={open => !open && resetCreateState()}>
      <DropdownMenuTrigger asChild>
        <Button variant="second-white" size={size} disabled={disabled || isLoading || isCreating}>
          Add to Sub-collection
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72">
        {subcollections.map(s => (
          <DropdownMenuItem key={s.id} onClick={() => onPick(s.id, s.name)}>
            {s.name} ({s.member_count})
          </DropdownMenuItem>
        ))}
        {subcollections.length === 0 && (
          <div className="px-2 py-2 text-sm text-muted-foreground">No sub-collections yet</div>
        )}

        <DropdownMenuSeparator />

        {isCreatingSubcollection ? (
          <div className="space-y-2 p-2" onKeyDown={e => e.stopPropagation()}>
            <label className="text-xs font-medium text-muted-foreground">New sub-collection</label>
            <input
              type="text"
              value={newSubcollectionName}
              onChange={e => setNewSubcollectionName(e.target.value)}
              placeholder="Sub-collection name"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-gray-400"
              autoFocus
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onCreate();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  resetCreateState();
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="outline" onClick={resetCreateState}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={onCreate} disabled={!newSubcollectionName.trim() || isCreating}>
                {isCreating ? "Adding..." : "Add"}
              </Button>
            </div>
          </div>
        ) : (
          <DropdownMenuItem
            className="font-medium"
            onSelect={e => {
              e.preventDefault();
              setIsCreatingSubcollection(true);
            }}
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            Create new sub-collection
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
